export type AnnotationKind = "highlight" | "strike"

export type AnnotationRange = {
  kind: AnnotationKind
  start: number
  end: number
}

export type QuestionAnnotations = Record<string, AnnotationRange[]>

const STORAGE_PREFIX = "quiz-annotations:v1"

export function getQuestionAnnotationsStorageKey(params: {
  attemptId: string
  questionId: string
}) {
  return `${STORAGE_PREFIX}:${params.attemptId}:${params.questionId}`
}

export function readQuestionAnnotations(params: {
  attemptId: string
  questionId: string
}): QuestionAnnotations {
  const key = getQuestionAnnotationsStorageKey(params)
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return {}
    return parsed as QuestionAnnotations
  } catch {
    return {}
  }
}

export function writeQuestionAnnotations(
  params: { attemptId: string; questionId: string },
  annotations: QuestionAnnotations,
) {
  const key = getQuestionAnnotationsStorageKey(params)
  try {
    sessionStorage.setItem(key, JSON.stringify(annotations))
  } catch {
    // Best-effort only.
  }
}

function normalizeRange(
  range: Pick<AnnotationRange, "start" | "end">,
  maxLength: number,
) {
  const start = Math.max(0, Math.min(range.start, maxLength))
  const end = Math.max(0, Math.min(range.end, maxLength))
  if (start === end) return null
  return start < end ? { start, end } : { start: end, end: start }
}

function mergeIntervals(intervals: Array<{ start: number; end: number }>) {
  if (intervals.length === 0) return []
  const sorted = [...intervals].sort(
    (a, b) => a.start - b.start || a.end - b.end,
  )
  const merged: Array<{ start: number; end: number }> = []
  for (const interval of sorted) {
    const last = merged[merged.length - 1]
    if (!last) {
      merged.push({ ...interval })
      continue
    }
    if (interval.start <= last.end) {
      last.end = Math.max(last.end, interval.end)
      continue
    }
    merged.push({ ...interval })
  }
  return merged
}

function subtractInterval(
  interval: { start: number; end: number },
  remove: { start: number; end: number },
) {
  if (remove.end <= interval.start || remove.start >= interval.end) {
    return [interval]
  }
  const segments: Array<{ start: number; end: number }> = []
  if (remove.start > interval.start) {
    segments.push({
      start: interval.start,
      end: Math.min(remove.start, interval.end),
    })
  }
  if (remove.end < interval.end) {
    segments.push({
      start: Math.max(remove.end, interval.start),
      end: interval.end,
    })
  }
  return segments.filter((segment) => segment.start < segment.end)
}

function normalizeRanges(
  ranges: AnnotationRange[],
  kind: AnnotationKind,
  maxLength: number,
) {
  const intervals = ranges
    .filter((r) => r.kind === kind)
    .map((r) => normalizeRange(r, maxLength))
    .filter((r): r is { start: number; end: number } => Boolean(r))
  return mergeIntervals(intervals).map((interval) => ({ ...interval, kind }))
}

export function addAnnotationToBlock(params: {
  existing: QuestionAnnotations
  blockId: string
  kind: AnnotationKind
  start: number
  end: number
  maxLength: number
}): QuestionAnnotations {
  const normalized = normalizeRange(
    { start: params.start, end: params.end },
    params.maxLength,
  )
  if (!normalized) return params.existing

  const existingRanges = params.existing[params.blockId] ?? []
  const nextRanges = normalizeRanges(
    [...existingRanges, { ...normalized, kind: params.kind }],
    params.kind,
    params.maxLength,
  )
  const keptOther = existingRanges.filter((r) => r.kind !== params.kind)

  return {
    ...params.existing,
    [params.blockId]: [...keptOther, ...nextRanges].sort(
      (a, b) => a.start - b.start || a.end - b.end,
    ),
  }
}

export function removeAnnotationFromBlock(params: {
  existing: QuestionAnnotations
  blockId: string
  kind: AnnotationKind
  start: number
  end: number
  maxLength: number
}): QuestionAnnotations {
  const normalized = normalizeRange(
    { start: params.start, end: params.end },
    params.maxLength,
  )
  if (!normalized) return params.existing

  const existingRanges = params.existing[params.blockId] ?? []
  const sameKindIntervals = existingRanges
    .filter((r) => r.kind === params.kind)
    .map((r) => normalizeRange(r, params.maxLength))
    .filter((r): r is { start: number; end: number } => Boolean(r))

  const nextSameKind = mergeIntervals(
    sameKindIntervals.flatMap((interval) =>
      subtractInterval(interval, normalized),
    ),
  ).map((interval) => ({ ...interval, kind: params.kind }))

  const keptOther = existingRanges.filter((r) => r.kind !== params.kind)

  return {
    ...params.existing,
    [params.blockId]: [...keptOther, ...nextSameKind].sort(
      (a, b) => a.start - b.start || a.end - b.end,
    ),
  }
}

export function clearAllQuestionAnnotations(): QuestionAnnotations {
  return {}
}
